import useAuth from '@/hooks/useAuth';
import { useEditCalendarMutation } from '@/queries/calendar.queries';
import { useListOrganizations } from '@/queries/organizations.queries';
import { ICalendar, ICalendarOwner } from '@/types/ICalendar';
import { IOrganization } from '@/types/IOrganization';
import { useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';

interface IArgs {
	calendar: ICalendar;
	onClose: () => void;
}

export function useEditCalendarController(args: IArgs) {
	const auth = useAuth();
	const orgsQuery = useListOrganizations();
	const editCalendarMutation = useEditCalendarMutation();
	const toast = useToast();

	const form = useForm({ defaultValues: args.calendar });

	const handleSubmit = form.handleSubmit((values) => {
		return editCalendarMutation.mutateAsync({ ...args.calendar, ...values }).then(() => {
			toast({
				status: 'success',
				title: t('Calendar updated'),
			});
			args.onClose();
		});
	});

	const options =
		orgsQuery.data?.organizations
			// Backend only allows to set calendar if the user is admin of the org
			.filter((org) => {
				const orgMembership = auth.getCurrentUser().organizationMemberships.find((om) => om.organizationId === org.id);
				if (!org) {
					return false;
				}
				return orgMembership?.roles.some((value) => value === 'ADMIN');
			})
			.map((org) => ({
				id: org.id,
				name: org.name,
				type: 'CalendarOwnerOrg' as ICalendarOwner['type'],
			})) ?? [];

	options.push({
		id: auth.getCurrentUser().userId,
		name: auth.getCurrentUser().email,
		type: 'CalendarOwnerUser',
	});

	return {
		selectedOption: options.find((option) => option.id === form.watch('owner').id),
		options,
		orgs: orgsQuery.data?.organizations ?? ([] as Readonly<Array<IOrganization>>),
		form,
		handleSubmit,
	};
}
